import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3500';
import { doGetDocumentContent, luHandleAppiError, u3500NormalizeApiError } from '../U3500Service';

export interface UseU3500DocumentContentHandlersParams {
  onSuccess?: (blob: Blob) => void;
  onError?: (error: unknown) => void;
}

/**
 * Handler : stream PDF aperçu (GET U3500/documents/:id/content, long-poll).
 * Ne pas appeler pour les fichiers non previewable.
 */
export function useU3500DocumentContentHandlers(params: UseU3500DocumentContentHandlersParams = {}) {
  const { onSuccess, onError } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadContent = useCallback(
    async (documentId: string): Promise<Blob | null> => {
      if (!documentId?.trim()) {
        return null;
      }
      try {
        const response = await doGetDocumentContent(axios, documentId.trim());
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: 'application/pdf' });
        onSuccess?.(blob);
        return blob;
      } catch (error: unknown) {
        const normalized = await u3500NormalizeApiError(error);
        const err = U2000HandleApiError(
          normalized as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (err.genericErrors?.length) {
          err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        }
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onError, t, toast]
  );

  return { loadContent };
}
